import { Fragment, h, render } from "preact";
import { useEffect, useState, useRef } from "preact/hooks";
import stockMessage from "../../helpers/stock-message";
import FullDetails from "../full-details";
import findCompatibleVariants from "../../helpers/findCompatibleVariants";
import SwatchImage from "../swatchImage";
import OptionValuePrice from "../optionValuePrice";
import { isColor, findColorOptionName } from "../../helpers/colors";
import AliasedOption from "../aliased-option";

const BundleConfigratorVariantPicker = ({ data, product, form }) => {
  const productId = product.id.split("/").pop();
  let variants = product?.variants?.edges;
  const colors = product.options.filter((o) => {
    return isColor(o.name);
  })[0];

  const hasAliases = product?.variants.edges.some(
    (edge) => edge.node.variantAliases,
  );

  const options = product?.options?.filter(
    (option) =>
      !isColor(option.name) &&
      option.name != "Default Title" &&
      option.name != "Title",
  );

  const colorOptionName = findColorOptionName(product?.options);

  let { compatibleSizes, compatibleModels } = data;

  const {
    compatibleVariants,
    updatedCompatibleSizes,
    updatedCompatibleModels,
  } = findCompatibleVariants({ variants, compatibleSizes, compatibleModels });

  variants = compatibleVariants;
  compatibleSizes = updatedCompatibleSizes;
  compatibleModels = updatedCompatibleModels;

  const initialVariant = variants[0]?.node;

  // Set Initial Variant Options
  let variantOptions = product?.options.map((edge, optionIndex) => ({
    [edge?.name]:
      hasAliases && optionIndex === options.length - 1
        ? null
        : initialVariant?.selectedOptions?.find(
            (element) => element.name == [edge?.name],
          ).value,
  }));

  const [variant, setVariant] = useState(initialVariant);
  const [selectedOptions, setSelectedOptions] = useState(variantOptions);

  const handleColorVariants = () => {
    if (!colors) return;
    let so = Object.assign({}, ...selectedOptions);
    let vs = so
      ? variants.filter((v) => {
          let vo = product?.options.map((edge) => ({
            [edge?.name]: v?.node?.selectedOptions.find(
              (element) => element.name == [edge?.name],
            ).value,
          }));
          let voc = Object.assign({}, ...vo);
          return JSON.stringify(voc["Size"]) === JSON.stringify(so["Size"]);
        })
      : variants;
    return vs;
  };

  const [colorVariants, setColorVariants] = useState(handleColorVariants);

  useEffect(() => {
    setColorVariants(handleColorVariants());
  }, [selectedOptions]);

  useEffect(() => {
    form.onBundleVariantChange();
  }, [variant]);

  const handleVariantChange = (option, val) => {
    let sv = null,
      svo = null,
      sp = null;
    if (val.node) {
      sp = val.node;
      sv = val.node.variants.edges[0].node;
      svo = val.node.variants.edges[0].node.selectedOptions;
    } else {
      const selectedOption = selectedOptions.map((o) => {
        if (Object.keys(o) == option) {
          return {
            ...o,
            [option]: val,
          };
        }
        return o;
      });
      const currentOption = {
        [option]: val,
      };

      let vset = variants;

      let selectedVariant = vset.filter((v) => {
        let vo = product?.options.map((edge) => ({
          [edge?.name]: v?.node?.selectedOptions.find(
            (element) => element.name == [edge?.name],
          ).value,
        }));
        return JSON.stringify(vo) === JSON.stringify(selectedOption);
      });

      if (!selectedVariant[0]?.node) {
        selectedVariant = vset.filter((v) => {
          let set = v?.node?.selectedOptions.find(
            (element) => element.name == [option] && element.value == [val],
          );
          return set?.value === currentOption[option];
        });
      }

      sv = selectedVariant.length > 0 ? selectedVariant[0] : selectedVariant;
      svo = sv?.node?.selectedOptions.map((option) => ({
        [option.name]: option.value,
      }));
      sv = sv.node ? sv.node : sv;
    }

    setSelectedOptions(svo);
    setVariant(sv);

    form.querySelectorAll("[data-add-message]").forEach((message) => {
      message.innerHTML =
        sv.preorder?.value === "true" ? "Pre-Order" : "Add to Cart";
    });

    if (variant === sv) return;
    let currentProduct = sp ? sp : null;
    form.dispatchEvent(
      new CustomEvent("product-form:update", {
        detail: {
          product: currentProduct,
          currentProduct,
        },
      }),
    );

    // form.currentProduct = currentProduct;
    // form.product = currentProduct;
  };

  const handleVariantActive = (name, val) => {
    let vo = Object.assign({}, ...selectedOptions);
    return vo[name] == val ? "--active" : "";
  };

  const selectedOptionValue = (name) => {
    let vo = Object.assign({}, ...selectedOptions);

    return vo[name] || null;
  };

  return (
    <div
      className="product-bundle__content-product"
      data-bundle-product={product.id}
    >
      <input
        data-variant-input
        type="hidden"
        name="id"
        value={variant.id.split("/").pop()}
        disabled
      />
      <div className="product-bundle__content-product-details">
        <a
          href={product.onlineStoreUrl}
          className="product-bundle__content-product-image"
        >
          <img
            src={window.getSizedImageUrl(
              product.images.edges[0].node.src,
              "200x",
            )}
            alt={product.title}
          />
        </a>
        <div className="product-bundle__content-product-text">
          <div className="product-bundle__content-product-title">
            {product.title}
          </div>
          <div
            className="product-bundle__content-product-availability"
            data-stock
          >
            {stockMessage(variant)}
          </div>
        </div>
        <FullDetails
          url={product.onlineStoreUrl}
          text="Details"
          className="product-bundle__content-product-view-details"
        />
      </div>
      {product.variants.edges.length > 1 && (
        <Fragment>
          {options?.map((option, index, arr) =>
            option.values.length > 1 &&
            hasAliases &&
            index === arr.length - 1 ? (
              <AliasedOption
                className="product-bundle__aliased-option"
                product={product}
                option={option}
                selectedOptions={selectedOptions}
                handleVariantChange={handleVariantChange}
                active={true}
                direction="up"
              ></AliasedOption>
            ) : (
              <div
                className={`product-bundle__content-variants ${
                  (option?.name == "Size" && compatibleSizes.length) ||
                  (option?.name == "Model" && compatibleModels.length)
                    ? "visually-hidden"
                    : ""
                }`}
              >
                <p class="product-bundle__content-selected-option">
                  <span className="product-bundle__content-selected-option-label">
                    Select a {option?.name}:
                  </span>
                  <span className="product-bundle__content-selected-option-value">
                    {selectedOptionValue(option?.name)}
                  </span>
                </p>
                <div className={`product-bundle__options ${option?.name}`}>
                  {option.values?.map((item, i) => (
                    <Fragment>
                      <div className="product-form__option">
                        <input
                          type="radio"
                          id={`template--${option?.name}-${i}-${productId}`}
                          aria-label={option?.name}
                          value={item}
                          form={`form-${option.name}-${productId}`}
                          checked={handleVariantActive(option?.name, item)}
                        />

                        <label
                          for={`template--${option?.name}-${i}-${productId}`}
                          aria-label={item}
                          className="text"
                          onClick={() => {
                            handleVariantChange(option?.name, item);
                          }}
                        >
                          <div className="product-form__option-text">
                            <span>{item}</span>
                          </div>
                        </label>
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>
            ),
          )}
          {colorVariants?.length > 1 && (
            <div className="product-bundle__content-variants">
              <p class="product-bundle__content-selected-option">
                <span className="product-bundle__content-selected-option-label">
                  Select a {colors?.name}:
                </span>
                <span className="product-bundle__content-selected-option-value">
                  {selectedOptionValue(colorOptionName)}
                </span>
              </p>
              <div className={`product-bundle__options ${colors?.name}`}>
                {colorVariants?.map((item, i) => (
                  <Fragment>
                    <div className="product-form__option">
                      <input
                        type="radio"
                        id={`template--${colors?.name}-${i}-${product.id
                          .split("/")
                          .pop()}`}
                        name={colors?.name}
                        value={item.node.title.split(" / ").pop()}
                        form={`form-${colors.name}`}
                        checked={handleVariantActive(
                          colorOptionName,
                          item.node.title.split(" / ").pop(),
                        )}
                      />

                      <label
                        for={`template--${colors?.name}-${i}-${product.id
                          .split("/")
                          .pop()}`}
                        aria-label="Cruiser 2-Seater"
                        className="text product-form__option-label--color"
                        onClick={() => {
                          handleVariantChange(
                            colorOptionName,
                            item.node.title.split(" / ").pop(),
                          );
                        }}
                      >
                        <SwatchImage
                          product={product}
                          handle={product?.handle}
                          optionValue={item?.node?.title.split(" / ").pop()}
                          className="product-form__option-swatch"
                        />
                        <div className="product-form__option-text">
                          <span>{item.node.title.split(" / ").pop()}</span>
                          <span
                            class="product-form__option-availability"
                            data-stock
                          >
                            {stockMessage(item.node)}
                          </span>
                        </div>
                        <OptionValuePrice
                          variant={item.node}
                          selectedVariant={
                            variant?.node ? variant.node : variant
                          }
                        />
                      </label>
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          )}
        </Fragment>
      )}
    </div>
  );
};

export default BundleConfigratorVariantPicker;
